import { Ref, computed, watch } from "vue";

export interface StepConfig {
  step_name: number;
  title: string;
  titleKey: string;
  apiEndpoint?: string;
  preStepCallback?: (config: StepConfig) => Promise<boolean>;
  postStepCallback?: (config: StepConfig) => Promise<boolean>;
}

export function useStepFunctions(
  currentFlowSteps: Ref<StepConfig[]>,
  stepNumber: Ref<number>
) {
  const shownStep = computed(() => {
    const stepIndex = stepNumber.value - 1;
    return currentFlowSteps.value[stepIndex] || currentFlowSteps.value[0];
  });

  // A step the flow does not have (a hand-edited or old link, or a path with fewer steps) shows the
  // first step, so the URL goes back to it too: Back and Next count from the URL's step, and from
  // one past the end neither found a step to go to.
  watch(
    [stepNumber, () => currentFlowSteps.value.length],
    ([step, stepCount]) => {
      if (!Number.isInteger(step) || step < 1 || step > stepCount) {
        stepNumber.value = 1;
      }
    },
    { immediate: true }
  );

  const invokePreStepCallback = async (): Promise<boolean> => {
    if (shownStep.value.preStepCallback) {
      return shownStep.value.preStepCallback(shownStep.value);
    }
    return true;
  };

  // One step change at a time: a second click while the current step saves would save it twice.
  let changingStep = false;

  const setStep = async (targetStepNumber: number) => {
    if (changingStep) return;
    changingStep = true;
    try {
      await changeStep(targetStepNumber);
    } finally {
      changingStep = false;
    }
  };

  const changeStep = async (targetStepNumber: number) => {
    const currentStep = shownStep.value;
    const stepIndex = targetStepNumber - 1;
    const targetStep = currentFlowSteps.value[stepIndex];
    const goingBack = targetStepNumber < stepNumber.value;

    if (!targetStep) return;

    // Only a step forward saves the current step. Going back keeps its edits in the draft unsaved:
    // saving there created a company the user then left, and starting over beside it made a second.
    if (
      !goingBack &&
      currentStep.postStepCallback &&
      !(await currentStep.postStepCallback(currentStep))
    ) {
      return;
    }

    // Check pre-step callback for target step
    if (
      targetStep.preStepCallback &&
      !(await targetStep.preStepCallback(targetStep))
    ) {
      return;
    }

    // If all callbacks pass, update the step
    stepNumber.value = targetStepNumber;
  };

  const navCheck = (step: number) =>
    computed(() => {
      if (step === 1) return true;
      return stepNumber.value - step >= -1;
    });

  const navSteps = computed(() =>
    currentFlowSteps.value.map((stepConfig, index) => ({
      title: stepConfig.title,
      isAvailable: navCheck(index + 1).value,
    }))
  );

  return {
    shownStep,
    navSteps,
    setStep,
    invokePreStepCallback,
  };
}
